import type { AxiosInstance } from 'axios';
import authService from './auth.service';

export type InvitationStatus =
  | 'pending' | 'sent' | 'accepted' | 'failed' | 'skipped' | 'expired';

export type BatchStatus = 'queued' | 'processing' | 'completed' | 'failed';

export interface InvitationRowError {
  row_number: number;
  field?: string | null;
  value?: string | null;
  message: string;
}

export interface InvitationItemSummary {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  // Invitations are a lead-acquisition channel for prospective
  // investors only. Staff (agent / manager / super_admin) are created
  // through /admin/staff, never through the invitation flow — see
  // backend `invitation_schemas.InvitableRole`.
  assigned_role: 'client';
  status: InvitationStatus;
  skip_reason?: string | null;
  send_error?: string | null;
  sent_at?: string | null;
  accepted_at?: string | null;
}

export interface InvitationBatchSummary {
  id: string;
  status: BatchStatus;
  requested_role: 'client';
  file_name: string | null;
  total_count: number;
  sent_count: number;
  failed_count: number;
  skipped_count: number;
  accepted_count: number;
  correlation_id: string;
  created_at: string;
  updated_at: string;
}

export interface InvitationBatchDetail {
  batch: InvitationBatchSummary;
  items: InvitationItemSummary[];
}

/** Classification buckets returned by the dry-run preview. Each
 *  bucket has a count; the UI renders them in a vertical list with
 *  contextual messaging (e.g. closed-account count gets a red
 *  warning, new count gets a green tag). */
export type PreviewBucket =
  | 'new'
  | 'expired_invitation'
  | 'existing_user_active'
  | 'existing_user_closed'
  | 'already_pending'
  | 'already_invited'
  | 'already_converted'
  | 'previously_rejected'
  | 'internal_domain'
  | 'duplicate_in_file'
  | 'unsubscribed';

export interface BulkPreviewResponse {
  format_ok: boolean;
  vendor_source: string | null;
  total_rows: number;
  buckets: Partial<Record<PreviewBucket, number>>;
  parse_errors: InvitationRowError[];
}

export interface ComplianceWarning {
  kind: 'closed_account' | 'previously_rejected';
  email_masked: string;
  row_number: number;
  message: string;
}

export interface BulkCommitResponse {
  batch: InvitationBatchSummary;
  parse_errors: InvitationRowError[];
  compliance_warnings: ComplianceWarning[];
  /** Only where a rehearsal runs and only for a super admin: the address and link of each invitation made. */
  links: { email: string; link: string }[];
}

export interface VendorFields {
  vendor_source: string;
  vendor_acquired_at?: string;  // ISO YYYY-MM-DD
  vendor_cost?: string;          // decimal as string
  /** The basis the vendor states for having these contacts (#289). Required to commit. */
  consent_basis?: string;
  /** A reference to the vendor's evidence of that basis. Required to commit. */
  consent_evidence_ref?: string;
}

class InvitationsService {
  private get api(): AxiosInstance {
    return authService.getApi();
  }

  /** Dry-run validation. Returns bucket counts without writing
   *  anything. The UI's 2-step bulk form calls this first; on
   *  confirm it re-uploads the same file via `commitBulk`. */
  async previewBulk(file: File, vendor?: VendorFields): Promise<BulkPreviewResponse> {
    const form = new FormData();
    form.append('file', file);
    form.append('dry_run', 'true');
    if (vendor?.vendor_source) form.append('vendor_source', vendor.vendor_source);
    if (vendor?.vendor_acquired_at) form.append('vendor_acquired_at', vendor.vendor_acquired_at);
    if (vendor?.vendor_cost) form.append('vendor_cost', vendor.vendor_cost);
    const res = await this.api.post<BulkPreviewResponse>(
      '/api/v1/invitations/bulk',
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return res.data;
  }

  /** Commit. Vendor fields REQUIRED (server rejects empty
   *  vendor_source with 400). Returns the batch summary + any
   *  compliance warnings the UI should surface prominently. */
  async commitBulk(file: File, vendor: VendorFields): Promise<BulkCommitResponse> {
    const form = new FormData();
    form.append('file', file);
    form.append('dry_run', 'false');
    form.append('vendor_source', vendor.vendor_source);
    if (vendor.vendor_acquired_at) form.append('vendor_acquired_at', vendor.vendor_acquired_at);
    if (vendor.vendor_cost) form.append('vendor_cost', vendor.vendor_cost);
    if (vendor.consent_basis) form.append('consent_basis', vendor.consent_basis);
    if (vendor.consent_evidence_ref) {
      form.append('consent_evidence_ref', vendor.consent_evidence_ref);
    }
    const res = await this.api.post<BulkCommitResponse>(
      '/api/v1/invitations/bulk',
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return res.data;
  }

  async listBatches(limit = 20): Promise<InvitationBatchSummary[]> {
    const res = await this.api.get<InvitationBatchSummary[]>(
      '/api/v1/invitations/batches',
      { params: { limit } },
    );
    return res.data;
  }

  async getBatch(batchId: string): Promise<InvitationBatchDetail> {
    const res = await this.api.get<InvitationBatchDetail>(
      `/api/v1/invitations/batches/${batchId}`,
    );
    return res.data;
  }

  csvDownloadUrl(batchId: string): string {
    const base = this.api.defaults.baseURL ?? '';
    return `${base}/api/v1/invitations/batches/${batchId}/csv`;
  }
}

export default new InvitationsService();
